import { InfoPage } from '@/components/info-page';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { Button } from '@/components/ui/button';
import { getAssessments } from '@/lib/api-calls';
import { useErrorToast } from '@/lib/error-handling';
import { useDebouncedSearchState, validateRouteRole } from '@/lib/routing';
import { CreateEditAssessment } from '@/routes/_layout/lessons/assessments/-components/create-edit-assessment';
import { isAirAdmin } from '@airreading/common';
import { AssessmentWithContent } from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';
import { lessonsSectionTabs } from '../-components/tabs';

const assessmentsSearchSchema = z.object({
  query: z.string().optional().catch(''),
});

export const Route = createFileRoute('/_layout/lessons/assessments/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: AssessmentsIndex,
  validateSearch: assessmentsSearchSchema,
});

const columnHelper = createColumnHelper<AssessmentWithContent>();
const columns = [
  columnHelper.accessor('name', { header: 'Name' }),
  columnHelper.accessor('contents', {
    header: 'Contents',
    cell: ({ getValue }) => {
      return (
        <ul>
          {getValue().map((content) => (
            <li key={content.id}>
              {content.name} ({content.format})
            </li>
          ))}
        </ul>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    id: 'actions',
    header: '',
    cell: ({ getValue }) => {
      return (
        <CreateEditAssessment
          from={getValue()}
          key={getValue().id}
          trigger={
            <Button variant={'outline'} size={'sm'}>
              Edit
            </Button>
          }
        />
      );
    },
  }),
];

function assessmentMatchesQuery(assessment: AssessmentWithContent, query: string): boolean {
  const normalizedQuery = query.toLowerCase();
  if (assessment.name.toLowerCase().includes(normalizedQuery)) {
    return true;
  }
  return assessment.contents.some((content) => content.name.toLowerCase().includes(normalizedQuery));
}

function AssessmentsIndex() {
  const [[query, activeQuery], setQuery] = useDebouncedSearchState(Route.id, 'query');
  const { data: assessments = [], isPending, error, refetch } = useQuery(getAssessments());

  useErrorToast(error, 'Error loading assessments. Please try again.');

  // This page does not paginate, so frontend filtering is ok.
  const filteredAssessments = useMemo(() => {
    const normalizedQuery = activeQuery?.trim();
    if (!normalizedQuery) {
      return assessments;
    }
    return assessments.filter((assessment) => assessmentMatchesQuery(assessment, normalizedQuery));
  }, [assessments, activeQuery]);

  const resetFilters = useCallback(() => {
    setQuery(undefined);
  }, [setQuery]);

  const table = useReactTable({
    data: filteredAssessments,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      enableSorting: false,
    },
  });

  return (
    <>
      <InfoPage
        headerText="Assessments"
        headerActions={
          <>
            <CreateEditAssessment trigger={<Button>Create Assessment</Button>} />
          </>
        }
        tabs={lessonsSectionTabs()}
      >
        <EntityTable
          table={table}
          isLoading={isPending}
          hasError={!!error}
          emptyState={'No assessments found'}
          errorState={<TableErrorState refetchAction={refetch} />}
          filters={{
            filterLeftComponents: (
              <>
                <TableFilterInputField
                  placeholder="Search by assessment or content name"
                  value={query ?? ''}
                  onChange={(event) => {
                    setQuery(event.target.value);
                  }}
                />
                <TableFilterResetButton show={true} onClick={resetFilters} />
              </>
            ),
          }}
        />
      </InfoPage>
    </>
  );
}
